import 'chartjs-adapter-date-fns'
import React, { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import 'react-lazy-load-image-component/src/effects/blur.css'

import { PushPin, Summarize } from '@mui/icons-material'
import { Card, CardContent, Typography } from '@mui/material'

import { GameInfoDialog } from '../../../../components/GameInfoDialog/GameInfoDialog'
import { LockedFeature } from '../../../../components/LockedFeature/LockedFeature'
import { LockedFeatureId } from '../../../../types/LockedFeature'
import { RoleEnum } from '../../../account'
import { useFTUEVideosAccessCheck, useNewsFeedLimitedAccessCheck, useRoleCheck, useSoftLaunchAccessCheck } from '../../../account/hooks/roleHooks'
import { useIsOnFreePlan } from '../../../account/hooks/userHooks'
import FtueVideoFullscreenModal from '../../../ftue-videos/components/FtueVideoFullscreenModal/FtueVideoFullscreenModal'
import { SelectedChapterTag } from '../../../ftue-videos/components/GameChapterList/GameChapterList'
import { useGetFtueVideoForGame } from '../../../ftue-videos/hooks/useGetFtueVideoForGame'
import { Game } from '../../../game'
import { GameStageId } from '../../../game/types/GameStageId'
import { useNewsContentLimitedCheck } from '../../hooks/newsHooks'
import { useNewsCardTitle } from '../../hooks/unifiedNewsEntryHooks'
import { NewsEntryType, UnifiedNewsEntry } from '../../types/UnifiedNewsEntry'
import './NewsCard.scss'
import { NewsCardContentSections } from './NewsCardContentSections/NewsCardContentSections'
import { NewsCardCoverImage } from './NewsCardContentSections/NewsCardCoverImage/NewsCardCoverImage'
import { NewsCardHeader } from './NewsCardHeader/NewsCardHeader'
import { NewsCardRibbon } from './NewsCardRibbon/NewsCardRibbon'
import { NewsCardSubHeader } from './NewsCardSubHeader/NewsCardSubHeader'

interface Props {
  newsEntry: UnifiedNewsEntry
}

// News card types that should display the "Limited functionality and data!" banner for
// users with the news_feed_limited role. Other types (Editorial, Report, live events,
// MajorGameRelease, SoftLaunchNonAnalyzed) are handled by the dedicated rules in the useMemo below.
const LIMITED_ACCESS_LOCKED_BANNER_TYPES: NewsEntryType[] = [
  NewsEntryType.GpsUpdate,
  NewsEntryType.GpsNew,
  NewsEntryType.GpsOutside,
  NewsEntryType.SoftLaunch,
  NewsEntryType.EntryTop10,
  NewsEntryType.EntryTop20,
  NewsEntryType.EntryTop50,
  NewsEntryType.EntryTop100,
  NewsEntryType.EntryTop200,
  NewsEntryType.PerformanceChangeRevenue,
  NewsEntryType.PerformanceChangeDownload,
]

const LIMITED_ACCESS_LOCKED_FEATURE_ID_BY_TYPE: Partial<Record<NewsEntryType, LockedFeatureId>> = {
  [NewsEntryType.PerformanceChangeRevenue]: LockedFeatureId.GameUpdateImpacts,
  [NewsEntryType.PerformanceChangeDownload]: LockedFeatureId.GameUpdateImpacts,
}

const NewsCard: React.FC<Props> = ({ newsEntry }) => {
  const hasNewsFeedLimitedAccess = useNewsFeedLimitedAccessCheck()
  const hasAccessToSoftLaunch = useSoftLaunchAccessCheck()
  const hasAccessToFTUEVideos = useFTUEVideosAccessCheck()
  const isInternalUser = useRoleCheck(RoleEnum.internal)
  const gameId = newsEntry.feedEntry.gameId || newsEntry.feedEntry.entryData.gameId
  const rawShowLimitedContent = useNewsContentLimitedCheck(gameId)

  const mappedNewsEntryToGame = useMemo(
    () =>
      ({
        id: newsEntry.feedEntry.entryData.gameId || newsEntry.feedEntry.gameId,
        resolvedName: newsEntry.feedEntry.entryData.game,
        artist: newsEntry.feedEntry.entryData.artist,
        icons: [newsEntry.feedEntry.entryData.icon],
      } as Game),
    [newsEntry.feedEntry]
  )

  const { video } = useGetFtueVideoForGame(newsEntry.feedEntry.entryData.gameId, { skip: !newsEntry.hasFTUEVideo })

  const entryType = newsEntry.feedEntry.type
  const showGameHeader = entryType !== NewsEntryType.Editorial && entryType !== NewsEntryType.Report
  const isSoftLaunchGame = newsEntry.feedEntry.entryData.stageId === GameStageId.soft_launch

  const isOnFreePlan = useIsOnFreePlan()

  const isLiveEventEntry =
    entryType === NewsEntryType.LiveEventNewEventType || entryType === NewsEntryType.LiveEventAnalystNotes || entryType === NewsEntryType.LiveEventTrackedGame
  const showLimitedContent = hasNewsFeedLimitedAccess || (isLiveEventEntry && rawShowLimitedContent)

  const [title, subtitle] = useNewsCardTitle({ newsEntry })
  const [openFTUEVideo, setOpenFTUEVideo] = useState(false)
  const [openGameInfo, setOpenGameInfo] = useState(false)
  const [openLockedFTUEVideos, setOpenLockedFTUEVideos] = useState(false)
  const [selectedChapterTag, setSelectedChapterTag] = useState<SelectedChapterTag>({ chapterId: 0, tagId: '' })

  const [hideGameDetails, limitedFunctionalityBannerType] = useMemo(() => {
    // Soft Launch (non-analyzed) cards are locked for Limited Access users as well as for users
    // without the soft_launch role viewing a soft-launch game. Limited Access entries may not carry
    // a soft_launch stageId, so gate on hasNewsFeedLimitedAccess to guarantee the upgrade banner shows.
    if (entryType === NewsEntryType.SoftLaunchNonAnalyzed && (hasNewsFeedLimitedAccess || (isSoftLaunchGame && !hasAccessToSoftLaunch)))
      return [true, LockedFeatureId.SoftLaunch]
    if (entryType === NewsEntryType.MajorGameRelease && isOnFreePlan) return [true, LockedFeatureId.GameUnlock]
    if (isLiveEventEntry && showLimitedContent) return [false, LockedFeatureId.LiveEventsTracker]
    if (hasNewsFeedLimitedAccess && LIMITED_ACCESS_LOCKED_BANNER_TYPES.includes(entryType)) {
      return [false, LIMITED_ACCESS_LOCKED_FEATURE_ID_BY_TYPE[entryType] ?? LockedFeatureId.GameUnlock]
    }
    return [false, undefined]
  }, [entryType, isSoftLaunchGame, hasAccessToSoftLaunch, isOnFreePlan, isLiveEventEntry, showLimitedContent, hasNewsFeedLimitedAccess])

  const handleFTUEVideoOpen = (open: boolean) => {
    hasAccessToFTUEVideos && !rawShowLimitedContent ? setOpenFTUEVideo(open) : setOpenLockedFTUEVideos(open)
  }

  return (
    <div className="NewsCard">
      <Card className="Card">
        {isSoftLaunchGame && (
          <NewsCardRibbon sx={{ fontSize: 11 }}>
            <Trans i18nKey={'newsfeed-options:soft_launch'} />
          </NewsCardRibbon>
        )}

        {/* Editorial */}
        {entryType === NewsEntryType.Editorial && (
          <>
            <NewsCardRibbon>
              {newsEntry.feedEntry.entryData.sticky ? (
                <>
                  <PushPin sx={{ fontSize: '15px', position: 'absolute', left: '18px', top: '3px' }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 'bold', lineHeight: 1, marginLeft: '11px' }}>
                    <Trans i18nKey={'newsfeed:editorial_type'} />
                  </Typography>
                </>
              ) : (
                <Trans i18nKey={'newsfeed:editorial_type'} />
              )}
            </NewsCardRibbon>
            <NewsCardCoverImage isInternalUser={isInternalUser} newsEntry={newsEntry} altText={title} />
          </>
        )}
        {/* Report */}
        {entryType === NewsEntryType.Report && (
          <>
            <NewsCardRibbon backgroundColor="#273143" textColor="#fff">
              <>
                <Summarize sx={{ fontSize: '15px', position: 'absolute', left: '24px', top: '3px' }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', lineHeight: 1, marginLeft: '11px' }}>
                  <Trans i18nKey={'newsfeed:report_type'} />
                </Typography>
              </>
            </NewsCardRibbon>
            <NewsCardCoverImage isInternalUser={isInternalUser} newsEntry={newsEntry} altText={title} />
          </>
        )}
        <CardContent>
          {showGameHeader && (
            <NewsCardHeader
              isInternalUser={isInternalUser}
              newsEntry={newsEntry}
              hideGameDetails={hideGameDetails}
              onOpenGameInfo={() => setOpenGameInfo(true)}
              showLimitedContent={showLimitedContent}
            />
          )}
          <NewsCardSubHeader
            newsEntry={newsEntry}
            title={title}
            subtitle={subtitle}
            onFTUEVideoOpen={handleFTUEVideoOpen}
            showLimitedContent={showLimitedContent}
          />
          <NewsCardContentSections
            newsEntry={newsEntry}
            hideGameDetails={hideGameDetails}
            hasNewsFeedLimitedAccess={hasNewsFeedLimitedAccess}
            limitedFunctionalityBannerType={limitedFunctionalityBannerType}
            showLimitedContent={showLimitedContent}
          />
        </CardContent>
      </Card>

      {openFTUEVideo && !!video && (
        <FtueVideoFullscreenModal
          game={mappedNewsEntryToGame}
          position={0}
          video={video}
          onClose={() => {
            setOpenFTUEVideo(false)
          }}
          selectedChapterTag={selectedChapterTag}
          onChapterTagClick={setSelectedChapterTag}
        />
      )}
      {openLockedFTUEVideos && (
        <LockedFeature.Dialog lockedFeatureId={LockedFeatureId.FtueVideos} open={openLockedFTUEVideos} onClose={() => setOpenLockedFTUEVideos(false)} />
      )}

      {openGameInfo && (
        <GameInfoDialog
          game={mappedNewsEntryToGame}
          open={openGameInfo}
          onClose={() => {
            setOpenGameInfo(false)
          }}
        />
      )}
    </div>
  )
}

export default NewsCard
